/**
 * @fileoverview App Insights Page
 * @description Main page for the Application Insights timeline feature.
 *              Two tabs: Overview (pre-built charts) and KQL Query (advanced editor).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Tabs, Tab, Alert, Chip } from '@mui/material';
import { Activity } from 'lucide-react';
import {
  TimeRangeSelector,
  MetricsSummaryCards,
  OverviewTimeline,
  RequestsChart,
  ExceptionsChart,
  DependenciesChart,
  ModuleUsageAnalytics,
  PerformanceHeatmap,
  AIInsightsChat,
} from '../components';
import { appInsightsApi } from '../services/appInsightsApi';
import { DEFAULT_TIME_RANGE, AUTO_REFRESH_INTERVAL } from '../utils/timeRangeUtils';

const AppInsightsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [timeRange, setTimeRange] = useState(DEFAULT_TIME_RANGE);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resourceLink, setResourceLink] = useState(null);

  // Data state for each section
  const [overviewData, setOverviewData] = useState(null);
  const [requestsData, setRequestsData] = useState(null);
  const [exceptionsData, setExceptionsData] = useState(null);
  const [dependenciesData, setDependenciesData] = useState(null);

  const intervalRef = useRef(null);

  // ──────────────────────────────────────────
  // Data fetching
  // ──────────────────────────────────────────

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { timespan: timeRange };

    try {
      const [overview, requests, exceptions, dependencies] =
        await Promise.allSettled([
          appInsightsApi.getOverview(params),
          appInsightsApi.getRequests(params),
          appInsightsApi.getExceptions(params),
          appInsightsApi.getDependencies(params),
        ]);

      const extract = (result) =>
        result.status === 'fulfilled' ? result.value?.data || result.value : null;

      setOverviewData(extract(overview));
      setRequestsData(extract(requests));
      setExceptionsData(extract(exceptions));
      setDependenciesData(extract(dependencies));

      // Check if any failed
      const failures = [overview, requests, exceptions, dependencies]
        .filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        const msg = failures[0].reason?.message || 'Some queries failed';
        setError(msg);
      }
    } catch (err) {
      setError(err?.message || 'Failed to fetch App Insights data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // Fetch resource link on mount
  useEffect(() => {
    appInsightsApi
      .health()
      .then((resp) => {
        const data = resp?.data || resp;
        if (data?.resource_link) setResourceLink(data.resource_link);
        if (!data?.configured) {
          setError(
            'App Insights credentials are not configured. Set APP_INSIGHTS_APP_ID and APP_INSIGHTS_API_KEY in the backend .env file.'
          );
        }
      })
      .catch(() => {});
  }, []);

  // Fetch data on mount and when time range changes
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh && activeTab === 0) {
      intervalRef.current = setInterval(fetchAllData, AUTO_REFRESH_INTERVAL);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, activeTab, fetchAllData]);

  // ──────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Activity size={28} color="#01426A" />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#01426A' }}>
          Application Insights
        </Typography>
        <Chip label="Timeline" size="small" color="primary" variant="outlined" />
      </Box>

      {/* Error banner */}
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tab navigation */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Overview" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="Module Usage" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="Performance" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="AI Assistant" sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      {/* ── Overview Tab ── */}
      {activeTab === 0 && (
        <>
          <TimeRangeSelector
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
            autoRefresh={autoRefresh}
            onAutoRefreshToggle={setAutoRefresh}
            onManualRefresh={fetchAllData}
            loading={loading}
            resourceLink={resourceLink}
          />

          <MetricsSummaryCards data={overviewData} loading={loading} />
          <OverviewTimeline data={overviewData} loading={loading} timeRange={timeRange} />
          <RequestsChart data={requestsData} loading={loading} timeRange={timeRange} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <ExceptionsChart data={exceptionsData} loading={loading} />
            <DependenciesChart data={dependenciesData} loading={loading} />
          </Box>
        </>
      )}

      {/* ── AI Assistant Tab ── */}
      {activeTab === 3 && <AIInsightsChat />}

      {/* ── Module Usage Tab ── */}
      {activeTab === 1 && <ModuleUsageAnalytics />}

      {/* ── Performance Heatmap Tab ── */}
      {activeTab === 2 && <PerformanceHeatmap />}
    </Box>
  );
};

export default AppInsightsPage;
